import { v4 as uuidv4 } from 'uuid';
import { action, computed, observable, set } from 'mobx';
import { _noop } from '../../utils/lodash.utils';
import { IBaseContext, TCommand } from '../../types/builder.types';
import { IActionResult } from '~/types/server.types';

export enum ActionStatus {
    new = 'new',
    appliedLocally = 'appliedLocally',
    appliedResolved = 'appliedResolved',
    applyFailed = 'applyFailed',
    revertedLocally = 'revertedLocally',
    revertedResolved = 'revertedResolved',
    revertFailed = 'revertFailed',
    redoLocally = 'redoLocally',
    redoResolved = 'redoResolved',
    redoFailed = 'redoFailed'
}

export enum ActionRestrictions {
    none = 0,
    urgent = 1, // Make the actions queued to server to be flushed now.
    blocking = 3, // The client wait for resolve. It's by default an urgent restriction
    irreversible = 4 // Cannot be reverted.
}

export enum ServerActionType {
    UpdateFlow = 'UpdateFlow',
    UpdateFlowContent = 'UpdateFlowContent',
    CreateBlock = 'CreateBlock',
    UpdateBlockToTemplate = 'UpdateBlockToTemplate',
    UpdateBlock = 'UpdateBlock',
    UpdateBlockColumns = 'UpdateBlockColumns',
    ReplaceBlock = 'ReplaceBlock',
    DeleteBlock = 'DeleteBlock',
    CreatePage = 'CreatePage',
    UpdatePage = 'UpdatePage',
    DeletePage = 'DeletePage',
    UpdateVariable = 'UpdateVariable'
}

export interface IBasicCommandsActionValue {
    commands: TCommand[];
    revertCommands: TCommand[];
}

export interface IActionDataForServer {
    undo?: boolean;
    redo?: boolean;
    type?: ServerActionType;
    created_at?: Date;
    action_number?: number;

    [key: string]: any;
}

export interface ActionBaseDataForServer {
    _id?: string;
    clientType?: string;
    description?: string;
    type?: ServerActionType;
    applied_at?: Date;
    action_number?: number;
    action_numbers_map?: Record<number, boolean>;
    is_offline?: boolean;
}

export interface IBaseAction<TContext extends IBaseContext = {}> {
    // Props
    _id?: string;
    name?: string;
    description?: string;
    appliedAt?: Date;
    actionNumbersMap?: Record<number, boolean>; // All the action numbers the action had
    serverActionType: ServerActionType; // What type of data will arrive to the server
    restrictions?: ActionRestrictions; // Currently not in use
    wasAddedToTheOfflineQueue?: boolean;
    ctx: TContext; // The data that is required to perform the action

    // Actions
    applySideEffects?: () => void;
    revertSideEffects?: () => void;
    createApplyDataForServer: () => IActionDataForServer;
    createRevertDataForServer: () => IActionDataForServer;
    onApplyToServerResolved?: (result: any, data: any) => any;
    onRevertToServerResolved?: (result: any) => any;
    onApplyToServerFailed?: (actionError: ActionError) => any;
    onRevertToServerFailed?: (actionError: ActionError) => any;
}

export interface ISingleAction<TContext extends IBaseContext = {}>
    extends IBaseAction<TContext> {
    createCommands: () => void;
}

export interface Action<TContext extends IBaseContext>
    extends ISingleAction<TContext> {
    setActionNumber(number: number): void;
}

export interface IActionDataForServerResults {
    apply?: IActionDataForServer;
    revert?: IActionDataForServer;
}

export interface ActionError {
    error: any;
    actionResult?: IActionResult;
}

export class Action<TContext extends IBaseContext = {}>
    implements ISingleAction<TContext>
{
    _id?: string;
    @observable name?: string;
    @observable status: ActionStatus;
    @observable isSentToServer: boolean;
    @observable ctx: TContext;
    @observable action_number: number;
    dataForServer: IActionDataForServerResults;

    constructor() {
        this._id = uuidv4();
        this.status = ActionStatus.new;
        this.isSentToServer = false;
        this.name = this.serverActionType;
        this.restrictions = ActionRestrictions.none;
        this.dataForServer = {};
        this.actionNumbersMap = {};

        this.onApplyToServerResolved = _noop;
        this.onRevertToServerResolved = _noop;
    }

    @computed
    get isFailedStatus() {
        return (
            this.status === ActionStatus.applyFailed ||
            this.status === ActionStatus.revertFailed ||
            this.status === ActionStatus.redoFailed
        );
    }

    @computed
    get isLocalStatus() {
        return (
            this.status === ActionStatus.appliedLocally ||
            this.status === ActionStatus.revertedLocally ||
            this.status === ActionStatus.redoLocally
        );
    }

    @computed
    get isResolvedStatus() {
        return (
            this.status === ActionStatus.appliedResolved ||
            this.status === ActionStatus.revertedResolved ||
            this.status === ActionStatus.redoResolved
        );
    }

    @computed
    get isUndoAction() {
        return (
            this.status === ActionStatus.revertedLocally ||
            this.status === ActionStatus.revertedResolved ||
            this.status === ActionStatus.revertFailed
        );
    }

    init(actionData: ISingleAction<TContext>) {
        set(this, actionData);
    }

    applyCommands() {}

    revertCommands() {}

    rollbackCommandsLocally() {}

    getDataForServer() {
        if (this.status === ActionStatus.appliedLocally) {
            return this.dataForServer.apply;
        }

        if (this.status === ActionStatus.revertedLocally) {
            return { undo: true, ...this.dataForServer.revert };
        }

        if (this.status === ActionStatus.redoLocally) {
            return { redo: true, ...this.dataForServer.apply };
        }

        throw Error(
            `The action status was ${this.status} instead of local one.`
        );
    }

    onActionToServerResolved(result, data) {
        if (
            this.status === ActionStatus.appliedResolved ||
            this.status === ActionStatus.appliedLocally
        ) {
            return this.onApplyToServerResolved(result, data);
        }

        if (
            this.status === ActionStatus.revertedResolved ||
            this.status === ActionStatus.revertedLocally
        ) {
            return this.onRevertToServerResolved(result);
        }

        if (
            this.status === ActionStatus.redoResolved ||
            this.status === ActionStatus.redoLocally
        ) {
            return this.onApplyToServerResolved(result, data);
        }

        throw Error(
            `The action status was ${this.status} instead of local one.`
        );
    }

    @action
    setActionNumber(actionNumber: number) {
        this.action_number = actionNumber;
        this.actionNumbersMap[actionNumber] = true;
    }

    @action
    setStatus(newStatus: ActionStatus) {
        this.status = newStatus;
    }

    @action
    setIsSentToServer(isSentToServer: boolean) {
        this.isSentToServer = isSentToServer;
    }

    setToSuccess() {
        if (this.status === ActionStatus.appliedLocally) {
            this.setStatus(ActionStatus.appliedResolved);

            return;
        }

        if (this.status === ActionStatus.redoLocally) {
            this.setStatus(ActionStatus.redoResolved);

            return;
        }

        if (this.status === ActionStatus.revertedLocally) {
            this.setStatus(ActionStatus.revertedResolved);

            return;
        }

        if (
            this.status === ActionStatus.appliedResolved ||
            this.status === ActionStatus.revertedResolved ||
            this.status === ActionStatus.redoResolved
        ) {
            return;
        }

        throw Error(
            `The action status was ${this.status} instead of local one.`
        );
    }

    setToFailed() {
        if (
            this.status === ActionStatus.appliedLocally ||
            this.status === ActionStatus.appliedResolved
        ) {
            this.setStatus(ActionStatus.applyFailed);

            return;
        }

        if (
            this.status === ActionStatus.revertedLocally ||
            this.status === ActionStatus.revertedResolved
        ) {
            this.setStatus(ActionStatus.revertFailed);

            return;
        }

        if (
            this.status === ActionStatus.redoLocally ||
            this.status === ActionStatus.redoResolved
        ) {
            this.setStatus(ActionStatus.redoFailed);

            return;
        }

        if (
            this.status === ActionStatus.applyFailed ||
            this.status === ActionStatus.revertFailed ||
            this.status === ActionStatus.redoFailed
        ) {
            return;
        }

        throw Error(
            `The action status was ${this.status} instead of local one.`
        );
    }

    actionFailedInServer(actionError: ActionError) {
        if (!this.isSentToServer) {
            throw Error(
                `The action wasn't sent to the server with status ${this.status}.`
            );
        }

        if (
            this.status === ActionStatus.applyFailed ||
            this.status === ActionStatus.redoFailed
        ) {
            if (this.onApplyToServerFailed) {
                this.onApplyToServerFailed(actionError);
            }

            return;
        }

        if (this.status === ActionStatus.revertFailed) {
            if (this.onRevertToServerFailed) {
                this.onRevertToServerFailed(actionError);
            }

            return;
        }

        throw Error(
            `The action status was ${this.status} instead of failed one.`
        );
    }

    getBasicDataForServer(): ActionBaseDataForServer {
        return {
            _id: this._id,
            clientType: this.name,
            description: this.description,
            type: this.serverActionType,
            applied_at: this.appliedAt,
            action_number: this.action_number,
            action_numbers_map: this.actionNumbersMap,
            is_offline: this.wasAddedToTheOfflineQueue
        };
    }

    setContext(ctx: Partial<TContext>) {
        set(this.ctx, ctx);
    }
}
